/**
 * The encounter director.
 *
 * This is the part of the game that decides how often anything happens, and the
 * answer is: rarely. Pressure accrues while the player is safe, and when it
 * finally crosses the threshold the most likely outcome by a wide margin is a
 * fakeout — a thud two rooms away, a shape at the end of a corridor that is
 * gone when you get there. Nothing behind it.
 *
 * Underneath that, a slow ambient heartbeat of distant noises runs regardless
 * of pressure. Most of what a player hears in a run comes from there.
 *
 * The one place that rate is not rare is a slide. See `surge.ts`: the numbers
 * below describe the whole game apart from someone walking at the way down, and
 * the surge changes them there and nowhere else — it multiplies the rate around
 * the mouth, and once per approach it buys a hunter outright rather than
 * waiting for one to be rolled.
 *
 * There is one other thing here that pressure does not pay for, and it is
 * `updatePack`: kinds whose whole design is that they are never met alone
 * cannot be bought with a currency that stops accruing the moment one of them
 * is engaged.
 */

import { TUNING } from '../core/config';
import { bfsDistances, hasLineOfSight, UNREACHABLE } from '../core/grid';
import { clamp01 } from '../core/math';
import { makeRNG } from '../core/rng';
import type { Cell2, EntityKind, GeneratedLevel, Grid, RNG, SfxName, Vec2 } from '../core/types';
import type { Agent, EntityWorld } from './brain';
import { getEntityDef } from './defs';
import { corridorScore } from './nav';
import { facingCos, playerCanSee, PLAYER_HALF_FOV } from './perception';
import { makeSlideSurge, type SlideSurge } from './surge';

/** What the director is allowed to do to the world. */
export interface DirectorHost {
  /** Live agents, for budget and "is something already happening" checks. */
  readonly agents: readonly Agent[];
  /**
   * Place an entity. Returns the live agent, or null if the world was not ready
   * — the director needs it back so a surge spawn can be marked as a long hunt
   * before anything has had a chance to think.
   */
  spawn(kind: EntityKind, pos: Vec2): Agent | null;
  /**
   * Take one away. Only ever used on something the player has never seen and
   * cannot currently see, to make room inside the level's budget for a spawn
   * that matters more than the one standing there; see `makeRoom`.
   */
  despawn(id: number): void;
}

export interface Director {
  setLevel(level: GeneratedLevel, levelIndex: number): void;
  update(w: EntityWorld): void;
  /** Called by the system when an engaged entity calms down or is recycled. */
  encounterResolved(): void;
  /** 0..1 progress toward the next roll. Diagnostic only. */
  readonly charge: number;
  /**
   * 0..1 dread coming from the slide surge rather than from any entity. The
   * entity system folds it into `threat`, which is what the audio layer's
   * tension is driven from — it is how a surge announces itself before the
   * first thing it summons has arrived.
   */
  readonly surgeTension: number;
  /** A new run started. Per-run teaching is owed again. */
  newRun(): void;
}

/**
 * Relative likelihood of each kind when a real spawn does happen.
 *
 * The dread kinds still out-weigh the hunters: a level whose roster holds both
 * should mostly produce the thing that stands at the end of a corridor, and the
 * thing that runs at you should be the surprise. What changed is that the thing
 * that runs at you now arrives when it is drawn.
 */
const SPAWN_WEIGHT: Record<EntityKind, number> = {
  smiler: 1.4,
  faceling: 1.2,
  bacteria: 1.1,
  partygoer: 1.1,
  hound: 1.0,
  centipede: 1.0,
  deathmoth: 0.9,
  clump: 0.5,
  // Never bought with threat budget; hallucinations come from sanity instead.
  hollow: 0,
};

const AMBIENT_SFX: readonly SfxName[] = ['distant-thud', 'distant-scrape'];

/**
 * The kinds a slide surge may summon from across the floor. Documented beside
 * the roster in `TUNING.entities.surgeSpawn.kinds`; bound to a typed name here
 * so a kind that does not exist is a compile error rather than a hunt that
 * silently never happens. Narrower than `TUNING.entities.pursuit.kinds` on
 * purpose — see the note there.
 */
const HUNT_KINDS: readonly EntityKind[] = TUNING.entities.surgeSpawn.kinds;

/**
 * The pack, bound to typed names the same way and for the same reason. See
 * `TUNING.entities.pack`: these are the kinds the player must never be able to
 * meet one at a time.
 */
const PACK = TUNING.entities.pack;
const PACK_KINDS: readonly EntityKind[] = PACK.kinds;

/** Four-connected offsets, for walking back down a BFS field. */
const STEP_X = [1, -1, 0, 0] as const;
const STEP_Y = [0, 0, 1, -1] as const;

/** Cells the spawn search will consider before giving up. */
const MAX_CANDIDATES = 384;
/** Candidate cells a surge placement scores before taking the best it saw. */
const HUNT_SAMPLES = 72;
/** Seconds between low-sanity hallucination rolls, and the sanity that gates them. */
const HALLUCINATION_INTERVAL = 11;
const HALLUCINATION_SANITY = 40;
/** Cells from the level's start point that stay clear of director spawns. */
const SPAWN_KEEPOUT_CELLS = 6;

class DirectorImpl implements Director {
  private level: GeneratedLevel | null = null;
  private rng: RNG = makeRNG(1);

  private pressure = 0;
  private grace = 0;
  private ambientTimer = 0;
  private hallucinationTimer = HALLUCINATION_INTERVAL;
  /** Seconds until the next pack member answers the scream. See `updatePack`. */
  private packTimer = PACK.first;

  /** Reused by the spawn search; sized once, never per candidate. */
  private readonly candidates = new Int32Array(MAX_CANDIDATES);
  private readonly source: Cell2[] = [{ cx: 0, cy: 0 }];
  private readonly scratch: Vec2 = { x: 0, z: 0 };

  /** The slide surge. Inert until the player walks up to a way down. */
  private readonly surge: SlideSurge;

  constructor(private readonly host: DirectorHost) {
    // The surge places its noises with the director's own helper: there is one
    // idea in this file of where something may be heard from, and it applies to
    // the surge as well.
    this.surge = makeSlideSurge({
      agents: host.agents,
      offscreenSpot: (w, minR, maxR, behind) => this.offscreenSpot(w, minR, maxR, behind),
    });
  }

  get charge(): number {
    return clamp01(this.pressure / TUNING.director.spawnThreshold);
  }

  get surgeTension(): number {
    return this.surge.tension;
  }

  newRun(): void {
    this.surge.newRun();
  }

  setLevel(level: GeneratedLevel, levelIndex: number): void {
    this.level = level;
    this.rng = makeRNG(level.seed ^ Math.imul(levelIndex + 1, 0x9e3779b9));
    this.pressure = 0;
    this.grace = TUNING.director.gracePeriod;
    // Taking a slide resets the surge outright: the next floor starts on its
    // normal grace period, with nothing carried down the chute. Seeded from the
    // director's seed rather than forked off its stream, so a level the player
    // crosses without meeting a slide draws exactly what it drew before.
    this.surge.setLevel(level, this.rng.seed);
    // The first ambient noise lands well after the grace period, so arriving on
    // a level is genuinely quiet.
    const [ambMin, ambMax] = TUNING.director.ambientEventInterval;
    this.ambientTimer = this.rng.range(ambMin, ambMax);
    this.hallucinationTimer = HALLUCINATION_INTERVAL;
    this.packTimer = PACK.first;
  }

  encounterResolved(): void {
    // Withhold, rather than reset: after something has happened the level goes
    // quiet again for a long stretch.
    this.pressure = Math.min(this.pressure, -TUNING.director.postEncounterCooldown);
    this.surge.forget();
  }

  update(w: EntityWorld): void {
    const level = this.level;
    if (!level) return;
    const dt = w.ctx.dt;

    // Ahead of the grace check, because the surge is what burns grace down: a
    // player who lands beside a slide must not be immune to it for longer than
    // the stand-off it is meant to create. It is told whether this director is
    // actually accruing — during grace, and while something is already engaged,
    // it builds silently rather than talking over what is happening.
    const busy = this.engagedCount() > 0;
    this.surge.update(w, this.grace <= 0 && !busy);

    if (this.grace > 0) {
      this.grace -= dt * this.surge.graceScale;
      return; // absolute silence
    }

    this.ambientTimer -= dt;
    if (this.ambientTimer <= 0) {
      const [ambMin, ambMax] = TUNING.director.ambientEventInterval;
      this.ambientTimer = this.rng.range(ambMin, ambMax);
      this.ambientOneShot(w);
    }

    this.hallucinationTimer -= dt;
    if (this.hallucinationTimer <= 0) {
      this.hallucinationTimer = HALLUCINATION_INTERVAL;
      this.maybeHallucinate(w);
    }

    // Ahead of the busy check on purpose, and it is the only thing that is.
    // The pack is not bought with pressure — it is the rest of an encounter
    // that is already happening, and "nothing accrues while something is
    // happening" is exactly the rule that made a Partygoer arrive alone.
    this.updatePack(w);

    // Nothing accrues while something is already happening.
    if (busy) return;

    const loud = w.player.noise > 0.5 ? TUNING.director.noisePressureScale : 1;
    const base = TUNING.director.pressurePerSecond * dt * level.spec.threatScale * loud;
    // Near a slide this is several times `base`; away from one it is `base`,
    // minus whatever the surge still owes back. See `surge.ts`.
    this.pressure += this.surge.step(base, dt);
    if (this.pressure < TUNING.director.spawnThreshold) return;
    this.pressure = 0;
    this.surge.forget();

    // The budget is checked inside `trySpawn` now, because an approach window's
    // roll is allowed to make room for itself and a fakeout is not.
    if (this.rng.chance(this.surge.fakeoutChance(TUNING.director.fakeoutChance)) || !this.trySpawn(w)) {
      this.fakeout(w);
    }
  }

  /* ---------------------------------------------------------------- *
   * The common case: nothing happens
   * ---------------------------------------------------------------- */

  /** The heartbeat. A noise from somewhere with nothing whatsoever behind it. */
  private ambientOneShot(w: EntityWorld): void {
    const spot = this.offscreenSpot(w, 16, 38, false);
    if (!spot) return;
    this.surge.holdCue();
    w.audio.play(this.rng.pick(AMBIENT_SFX), {
      pos: spot,
      gain: this.rng.range(0.4, 0.8),
      rate: this.rng.range(0.85, 1.15),
    });
  }

  /** A scare that costs the player nothing but nerve. */
  private fakeout(w: EntityWorld): void {
    // Under a surge these arrive every few seconds; the cue track has to give
    // way to them or the two noise sources sit on top of each other.
    this.surge.holdCue();
    const roll = this.rng.next();

    if (roll >= 0.58 && roll < 0.84) {
      // A figure at the end of a corridor behind them. It is a hollow: harmless,
      // and gone the second time they look at it.
      const spot = this.corridorSpotBehind(w, 9, 20);
      if (spot) {
        this.host.spawn('hollow', spot);
        w.events.emit('entity:fakeout', { pos: { x: spot.x, z: spot.z }, flavor: 'glimpse' });
        return;
      }
    } else if (roll >= 0.84) {
      // Something close, behind, and already over by the time they turn around.
      const spot = this.offscreenSpot(w, 5, 11, true);
      if (spot) {
        w.audio.play('distant-scrape', { pos: spot, gain: 1, rate: this.rng.range(1.05, 1.3) });
        w.events.emit('entity:fakeout', { pos: { x: spot.x, z: spot.z }, flavor: 'shadow' });
        return;
      }
    }

    // The default, and the fallback when there was nowhere to put the other two.
    // A roll that spends pressure must always produce something, or the quiet
    // stretches turn into dead air followed by a real spawn.
    const spot = this.offscreenSpot(w, 12, 30, false) ?? this.offscreenSpot(w, 8, 40, false);
    if (!spot) return;
    // The bus carries this one. Playing it here *as well* meant every ambient
    // fakeout fired two overlapping one-shots: the audio engine bridges
    // `entity:fakeout` itself, and its de-duplication only catches a direct play
    // that lands after the bridged one, not before.
    w.events.emit('entity:fakeout', { pos: { x: spot.x, z: spot.z }, flavor: 'sound' });
  }

  /**
   * A hollow conjured by a failing mind rather than by the threat budget. Only
   * while sanity is low, only one at a time, and never in the player's view.
   */
  private maybeHallucinate(w: EntityWorld): void {
    if (w.sanity > HALLUCINATION_SANITY) return;
    for (const a of this.host.agents) if (a.state.kind === 'hollow') return;

    const chance = 0.05 + 0.3 * (1 - clamp01(w.sanity / HALLUCINATION_SANITY));
    if (!this.rng.chance(chance)) return;

    const spot = this.corridorSpotBehind(w, 7, 18) ?? this.offscreenSpot(w, 7, 18, true);
    if (!spot) return;
    this.host.spawn('hollow', spot);
    // Space them out, or a bottomed-out sanity bar turns into a parade.
    this.hallucinationTimer = HALLUCINATION_INTERVAL * 2.5;
  }

  /* ---------------------------------------------------------------- *
   * The rare case: something is actually there
   * ---------------------------------------------------------------- */

  private trySpawn(w: EntityWorld): boolean {
    const level = this.level;
    if (!level) return false;

    // An approach window has promised the player something crossing the floor,
    // so it summons from the kinds that will actually cross it rather than
    // taking whatever the roster's weights hand back.
    const approach = this.surge.approaching;
    const kind = this.pickKind(level, approach);
    if (!kind) return false;

    if (this.realCount() >= level.spec.entityBudget && !(approach && this.makeRoom(w))) return false;

    // A surge spawn comes from across the floor; everything else comes from the
    // ordinary band. If the level is too small or too broken up to hold a cell
    // that far out, fall back rather than waste the roll — the surge still gets
    // its spawn, just not its approach.
    let long = this.wantsLongHunt(kind);
    let pos = long ? this.findSpawnCell(w, kind, true) : null;
    if (!pos) {
      long = false;
      pos = this.findSpawnCell(w, kind, false);
    }
    if (!pos) return false;

    const agent = this.host.spawn(kind, pos);
    if (!agent) return false;
    if (long) this.surge.beginHunt(w, agent);
    // The window bought a body, just not a narrated one — the level was too
    // folded to hold the long band, or something already carried a licence.
    // Either way the player got the thing they were promised, so the window is
    // spent; leaving it open would keep paying `approach.charge` and re-roll
    // every couple of seconds until it timed out.
    else if (approach) this.surge.spendWindow();
    return true;
  }

  /**
   * Take one thing off the floor so an approach hunt can have its slot.
   *
   * `entityBudget` is a cap on how much is happening at once, and a wanderer
   * seventy metres away that the player has never laid eyes on is not what is
   * happening — but it was enough to turn the walk up to a slide back into an
   * empty room, which is the complaint this whole file is answering.
   *
   * The bar is deliberately high: never something the player has seen, never
   * something they could see now, never something engaged or licensed, and
   * never anything inside the ordinary spawn band. Nothing vanishes out of a
   * player's world; the far side of the floor gets quieter so the near side can
   * have a monster in it.
   */
  private makeRoom(w: EntityWorld): boolean {
    let worst: Agent | null = null;
    let worstD = TUNING.entities.maxSpawnDistance;
    for (const a of this.host.agents) {
      const s = a.state;
      if (s.kind === 'hollow' || a.engaged || a.longHunt > 0 || a.sighted || s.visible) continue;
      if (s.mode === 'stalk' || s.mode === 'chase' || s.mode === 'attack' || s.mode === 'investigate') continue;
      if (s.distToPlayer <= worstD) continue;
      worstD = s.distToPlayer;
      worst = a;
    }
    if (!worst) return false;
    this.host.despawn(worst.state.id);
    return true;
  }

  /* ---------------------------------------------------------------- *
   * The pack
   * ---------------------------------------------------------------- */

  /**
   * The rest of them, answering the one that screamed.
   *
   * A pack kind's whole design is that it is never met alone, and the
   * director's "nothing accrues while something is engaged" rule made that
   * impossible: the first Partygoer to start stalking froze the pressure it
   * would have taken to buy a second. Measured on the Funrooms over half an
   * hour, one was alive for seventeen minutes of it and there was never a
   * second one.
   *
   * So this is a separate route onto the floor with its own gates, and it costs
   * no pressure — the pack is one encounter, not several. It only runs while a
   * member is genuinely working on the player, it stops at `PACK.size` bodies,
   * it never exceeds the level's own budget by more than `PACK.bonus`, and it
   * spaces arrivals over half a minute so they come from wherever they were
   * rather than appearing as a group. Placement is the ordinary out-of-sight
   * search, and what they are told is what `alertKin` tells a live one: the
   * caller's idea of where the player was, which they still have to walk to and
   * still have to make good on.
   */
  private updatePack(w: EntityWorld): void {
    const level = this.level;
    if (!level) return;
    const kind = this.packKind(level);
    if (!kind) return;

    let live = 0;
    let caller: Agent | null = null;
    for (const a of this.host.agents) {
      if (a.state.kind !== kind) continue;
      live++;
      const m = a.state.mode;
      if (!caller && (m === 'stalk' || m === 'chase' || m === 'attack')) caller = a;
    }
    if (!caller) {
      // Nobody is working on anyone. The next one to start gets the full lead.
      this.packTimer = PACK.first;
      return;
    }
    if (live >= Math.min(PACK.size, level.spec.entityBudget + PACK.bonus)) return;

    this.packTimer -= w.ctx.dt;
    if (this.packTimer > 0) return;
    this.packTimer = this.rng.range(PACK.interval[0], PACK.interval[1]);

    const pos = this.findSpawnCell(w, kind, false);
    if (!pos) return;
    const a = this.host.spawn(kind, pos);
    if (!a) return;
    // Hearsay, written the way every other call in this game is written: a
    // position and a loudness for its next think tick, plus the licence that
    // lets it cross a floor wider than `activeRadius` to act on it.
    const known = caller.state.lastKnown ?? w.player.pos;
    a.heardPos.x = known.x;
    a.heardPos.z = known.z;
    a.heardLoudness = PACK.answerLoudness;
    a.heardPending = true;
    a.longHunt = Math.max(a.longHunt, TUNING.entities.pursuit.licence);
  }

  /** The pack kind this level actually hosts, if any. At most one. */
  private packKind(level: GeneratedLevel): EntityKind | null {
    for (const k of PACK_KINDS) if (level.spec.entityKinds.includes(k)) return k;
    return null;
  }

  /**
   * Should this spawn be a long hunt — placed across the floor, licensed to
   * stay awake, and narrated the whole way in?
   *
   * Only while a surge is actually armed, so nothing about an ordinary
   * encounter changes. Only for something that will cross the floor and then
   * actually arrive — see `TUNING.entities.surgeSpawn.kinds`, which is a roster
   * and not a speed threshold for a reason. And only one at a time: two things
   * closing from opposite ends of the level is noise, not dread.
   */
  private wantsLongHunt(kind: EntityKind): boolean {
    if (!this.surge.armed) return false;
    if (!HUNT_KINDS.includes(kind)) return false;
    for (const a of this.host.agents) if (a.longHunt > 0) return false;
    return true;
  }

  /**
   * Weighted pick over the level's allowed kinds, biased away from repeats.
   *
   * `hunters` narrows it to the kinds that cross a floor and arrive, for the
   * one roll an approach window pays for: the cue track it starts is a promise,
   * and a smiler holding a dark spot ninety metres away does not keep it. If
   * the level has none of them the roster answers as usual — a floor whose
   * roster is all dread still gets its spawn, it just does not get a hunt.
   */
  private pickKind(level: GeneratedLevel, hunters = false): EntityKind | null {
    const roster = hunters ? level.spec.entityKinds.filter((k) => HUNT_KINDS.includes(k)) : level.spec.entityKinds;
    const pool = roster.length > 0 ? roster : level.spec.entityKinds;
    let total = 0;
    for (const k of pool) {
      total += this.weightFor(k);
    }
    if (total <= 0) return null;
    let r = this.rng.next() * total;
    for (const k of pool) {
      r -= this.weightFor(k);
      if (r <= 0) return k;
    }
    return null;
  }

  private weightFor(kind: EntityKind): number {
    let wgt = SPAWN_WEIGHT[kind];
    for (const a of this.host.agents) if (a.state.kind === kind) wgt *= 0.35;
    return wgt;
  }

  /**
   * A reachable cell far enough away, out of the player's view cone, dark
   * enough for a light-shy kind, and away from where the level starts.
   *
   * Reachability comes from a BFS over walkable cells rather than a straight
   * distance test, so nothing is ever placed on the far side of a wall it can
   * never path around.
   *
   * `long` swaps the ordinary band for the surge's, which sits well outside
   * `activeRadius`. That is only survivable because the caller hands the result
   * a `longHunt` licence; see `TUNING.entities.surgeSpawn`.
   */
  private findSpawnCell(w: EntityWorld, kind: EntityKind, long: boolean): Vec2 | null {
    const level = this.level;
    if (!level) return null;
    const grid = w.grid;
    const def = getEntityDef(kind);
    const cs = grid.cellSize;

    const pc = grid.worldToCell(w.player.pos.x, w.player.pos.z);
    this.source[0]!.cx = pc.cx;
    this.source[0]!.cy = pc.cy;
    // A full BFS is fine here: this runs once every couple of minutes at most.
    const dist = bfsDistances(grid, this.source);
    const fromSpawn = level.distanceFromSpawn.length === dist.length ? level.distanceFromSpawn : null;

    // The ordinary far edge stays inside `activeRadius`: something placed beyond
    // it would be asleep on arrival and the encounter would simply not happen.
    // It also stays inside a hunter's *ears*, which is the tighter bound and
    // the reason `maxSpawnDistance` exists. The surge's band is outside both on
    // purpose and pays for that with a licence — the two must not be confused,
    // so they are chosen here together.
    const band = TUNING.entities.surgeSpawn.distance;
    const minCells = (long ? band[0] : TUNING.entities.minSpawnDistance) / cs;
    const maxCells = Math.max(minCells + 2, (long ? band[1] : TUNING.entities.maxSpawnDistance) / cs);

    // Reservoir sample so the search cost does not grow with the level size and
    // the choice stays uniform over everything that qualifies.
    let seen = 0;
    for (let i = 0; i < dist.length; i++) {
      const d = dist[i]!;
      if (d === UNREACHABLE || d < minCells || d > maxCells) continue;
      if (fromSpawn && fromSpawn[i]! < SPAWN_KEEPOUT_CELLS) continue;
      if (seen < MAX_CANDIDATES) this.candidates[seen] = i;
      else {
        const j = this.rng.int(0, seen);
        if (j < MAX_CANDIDATES) this.candidates[j] = i;
      }
      seen++;
    }
    const count = Math.min(seen, MAX_CANDIDATES);
    if (count === 0) return null;

    const darkWanted = clamp01(1 - def.lightAversion);

    // A hunt is placed to be *seen* coming, not merely to exist. See
    // `TUNING.entities.surgeSpawn.routeSample`. It applies to anything an
    // approach window buys, including the ordinary-band fallback a level too
    // folded to hold the long band drops back to: that spawn is still the thing
    // the player was promised, and it still has to be in front of them.
    if (long || this.surge.approaching) {
      const seen = this.frameHunt(w, dist, count, darkWanted);
      if (seen) return seen;
    }

    let fallback: Vec2 | null = null;

    // Two passes: the first insists on the light rule, the second does not.
    for (let pass = 0; pass < 2; pass++) {
      for (let tries = 0; tries < 26; tries++) {
        const idx = this.candidates[this.rng.int(0, count - 1)]!;
        const cx = idx % grid.w;
        const cy = (idx / grid.w) | 0;
        this.scratch.x = (cx + 0.5) * cs;
        this.scratch.z = (cy + 0.5) * cs;

        if (!this.outOfSight(w, this.scratch)) continue;
        if (pass === 0 && def.lightAversion > 0 && w.lightAt(this.scratch) > darkWanted) {
          if (!fallback) fallback = { x: this.scratch.x, z: this.scratch.z };
          continue;
        }
        return { x: this.scratch.x, z: this.scratch.z };
      }
    }
    return fallback;
  }

  /**
   * Pick the cell an approach hunt should come from, or null when there is no
   * mouth being approached and nothing to frame it against.
   *
   * "Visible, not merely present" is the whole of this function. A hunter that
   * arrives behind the player through a wall is a noise and then teeth; one
   * that crosses a lit hall in front of them while they are still walking is
   * the scene the slide run is supposed to be. Three things buy that, in
   * descending order of how much they were measured to matter:
   *
   *  - *A sightline onto the route.* It can see the stretch of floor the player
   *    is about to be standing on, so there is a moment where the two are in
   *    the same room with nothing between them. This one decides it: a building
   *    made of rooms has plenty of floor that is ahead of the player and behind
   *    three walls.
   *  - *Ahead.* It comes from the half of the world the player is walking into,
   *    within `routeCos` of their own bearing to the mouth, so the approach
   *    happens in front of them rather than over their shoulder.
   *  - *Lit.* Bright enough at forty metres to be a shape rather than a smudge,
   *    while still respecting a light-shy kind's own ceiling — the band between
   *    the two is the dim room a silhouette reads best in.
   *
   * All three are preferences scored against each other rather than filters. A
   * folded building may have nothing that answers all of them, and a hunt that
   * arrives badly framed is worth far more than a roll spent on nothing.
   */
  private frameHunt(w: EntityWorld, dist: Uint16Array, count: number, lightCeiling: number): Vec2 | null {
    const grid = w.grid;
    const cs = grid.cellSize;
    const target = this.surge.target;
    if (!target) return null;

    const S = TUNING.entities.surgeSpawn;
    const a = this.routePoint(grid, dist, target, S.routeSample[0]);
    const b = this.routePoint(grid, dist, target, S.routeSample[1]);
    // The player's own bearing to the mouth, as a yaw, so `facingCos` can ask
    // "is this candidate in the direction they are heading" with the same
    // arithmetic the view-cone tests use.
    const routeYaw = Math.atan2(target.x - w.player.pos.x, -(target.z - w.player.pos.z));

    let best: Vec2 | null = null;
    let bestScore = 0;
    for (let tries = 0; tries < HUNT_SAMPLES; tries++) {
      const idx = this.candidates[this.rng.int(0, count - 1)]!;
      this.scratch.x = ((idx % grid.w) + 0.5) * cs;
      this.scratch.z = (((idx / grid.w) | 0) + 0.5) * cs;
      if (!this.outOfSight(w, this.scratch)) continue;

      let score = 1;
      if ((a && hasLineOfSight(grid, this.scratch, a)) || (b && hasLineOfSight(grid, this.scratch, b))) score += 5;
      if (facingCos(w.player.pos, routeYaw, this.scratch) >= S.routeCos) score += 3;
      const light = w.lightAt(this.scratch);
      if (light >= S.minLight) score += 2;
      if (light <= lightCeiling) score += 1;

      if (score > bestScore) {
        bestScore = score;
        best = { x: this.scratch.x, z: this.scratch.z };
        // Everything a placement can be. Nothing left to look for.
        if (score >= 12) break;
      }
    }
    return best;
  }

  /**
   * A point `frac` of the way along the player's route to `to`, in world space.
   *
   * Walked back down the BFS field the placement search already built, so it is
   * the route the player will actually take through the building rather than
   * the straight line, which on a floor made of rooms is usually inside a wall.
   */
  private routePoint(grid: Grid, dist: Uint16Array, to: Vec2, frac: number): Vec2 | null {
    const c = grid.worldToCell(to.x, to.z);
    if (!grid.inBounds(c.cx, c.cy)) return null;
    let idx = c.cy * grid.w + c.cx;
    let d = dist[idx];
    if (d === undefined || d === UNREACHABLE) return null;
    const want = d * frac;

    // Bounded: the field decreases by one per step, so this is the cell
    // distance and cannot loop.
    for (let guard = 0; d > want && guard < 1024; guard++) {
      const cx = idx % grid.w;
      const cy = (idx / grid.w) | 0;
      let next = -1;
      for (let i = 0; i < 4; i++) {
        const nx = cx + STEP_X[i]!;
        const ny = cy + STEP_Y[i]!;
        if (!grid.inBounds(nx, ny)) continue;
        const nd = dist[ny * grid.w + nx]!;
        if (nd === UNREACHABLE || nd >= d) continue;
        next = ny * grid.w + nx;
        d = nd;
      }
      if (next < 0) break;
      idx = next;
    }
    return grid.cellToWorld(idx % grid.w, (idx / grid.w) | 0);
  }

  /**
   * Nothing may ever appear where the player could watch it appear. Inside the
   * view cone the bar is much higher: `minSpawnDistanceInView` metres, which on
   * these levels is past the fog.
   */
  private outOfSight(w: EntityWorld, pos: Vec2): boolean {
    const dx = pos.x - w.player.pos.x;
    const dz = pos.z - w.player.pos.z;
    const d = Math.hypot(dx, dz);
    if (d >= TUNING.entities.minSpawnDistanceInView) return true;
    return !playerCanSee(w.grid, w.player, pos, TUNING.entities.minSpawnDistanceInView);
  }

  /* ---------------------------------------------------------------- *
   * Placement helpers
   * ---------------------------------------------------------------- */

  /** A walkable point the player cannot see, optionally behind them. */
  private offscreenSpot(w: EntityWorld, minR: number, maxR: number, behind: boolean): Vec2 | null {
    const grid = w.grid;
    for (let i = 0; i < 20; i++) {
      const ang = this.rng.next() * Math.PI * 2;
      const r = this.rng.range(minR, maxR);
      const x = w.player.pos.x + Math.cos(ang) * r;
      const z = w.player.pos.z + Math.sin(ang) * r;
      const c = grid.worldToCell(x, z);
      if (!grid.isWalkable(c.cx, c.cy)) continue;

      this.scratch.x = (c.cx + 0.5) * grid.cellSize;
      this.scratch.z = (c.cy + 0.5) * grid.cellSize;
      if (behind && facingCos(w.player.pos, w.player.yaw, this.scratch) > Math.cos(PLAYER_HALF_FOV)) continue;
      if (playerCanSee(grid, w.player, this.scratch, maxR + 4)) continue;
      return { x: this.scratch.x, z: this.scratch.z };
    }
    return null;
  }

  /** As `offscreenSpot`, but preferring somewhere with only one way out. */
  private corridorSpotBehind(w: EntityWorld, minR: number, maxR: number): Vec2 | null {
    let best: Vec2 | null = null;
    let bestScore = 99;
    for (let i = 0; i < 5; i++) {
      const p = this.offscreenSpot(w, minR, maxR, true);
      if (!p) continue;
      const c = w.grid.worldToCell(p.x, p.z);
      const score = corridorScore(w.grid, c.cx, c.cy);
      if (score < bestScore) {
        bestScore = score;
        best = p;
      }
      if (bestScore <= 2) break;
    }
    return best;
  }

  /* ---------------------------------------------------------------- *
   * Bookkeeping
   * ---------------------------------------------------------------- */

  private engagedCount(): number {
    let n = 0;
    for (const a of this.host.agents) {
      // Same rule `brain.setMode` uses for `engaged`: a stationary hazard
      // noticing you is not an encounter. Counting one would let an immobile
      // entity parked near a camping player suppress the director forever.
      if (a.def.walkSpeed <= 0) continue;
      const m = a.state.mode;
      if (m === 'stalk' || m === 'chase' || m === 'attack' || m === 'investigate') n++;
    }
    return n;
  }

  /** Hallucinations do not count against the level's entity budget. */
  private realCount(): number {
    let n = 0;
    for (const a of this.host.agents) if (a.state.kind !== 'hollow') n++;
    return n;
  }
}

export function makeDirector(host: DirectorHost): Director {
  return new DirectorImpl(host);
}
